/**
 * A seção «Custo com OCR» de Insights (06/10/2026): o que o dono acompanha para
 * saber quanto o Modo Texto (Google Vision) está custando.
 *
 * Tudo chega pronto do servidor — unidades somadas do livro-razão, o mês em
 * UTC, a estimativa em dólares pela tabela configurada. Aqui só se formata e se
 * dá nome às origens: nenhuma conta, nenhuma cor que mude sozinha.
 *
 * «Chamada» é a palavra da tela para a unidade do Google: uma imagem enviada ao
 * Vision. Uma página pode render mais de uma (partes grandes, releitura girada).
 */
import type { InsightsRange, OcrUsageSummary } from "./api";
import { formatUtcDate } from "./gcIndicators.ts";

export interface OcrCostCard {
  key: "month" | "range" | "previous_month" | "auto";
  label: string;
  value: string;
  subLabel: string;
}

const usdFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const integerFormatter = new Intl.NumberFormat("pt-BR");

/** `US$ 1,50`. */
export function formatUsd(value: number): string {
  return usdFormatter.format(value);
}

export function formatUnits(value: number): string {
  return integerFormatter.format(value);
}

export const RANGE_PHRASE: Record<InsightsRange, string> = {
  "24h": "nas últimas 24 h",
  "7d": "nos últimos 7 dias",
  "30d": "nos últimos 30 dias",
};

/** Quem pediu a análise, com a palavra da tela. */
export function originLabel(origin: string | null): string {
  switch (origin) {
    case "auto":
      return "Automático (todo arquivo)";
    case "admin":
      return "Botão «Identificar Texto»";
    case "integration":
      return "Integração";
    case "public_view":
      return "Link público";
    default:
      return "Antes do registro";
  }
}

/** A cor fixa de cada origem na barra «por quem pediu». */
export function originColorClass(origin: string | null): string {
  switch (origin) {
    case "auto":
      return "bg-emerald-400";
    case "admin":
      return "bg-blue-400";
    case "integration":
      return "bg-amber-300";
    case "public_view":
      return "bg-rose-300";
    default:
      return "bg-ink-500";
  }
}

function plural(n: number, singular: string, pluralForm: string): string {
  return `${formatUnits(n)} ${n === 1 ? singular : pluralForm}`;
}

function monthCard(o: OcrUsageSummary): OcrCostCard {
  const franquia =
    o.free_units_per_month > 0
      ? `${formatUnits(o.billable_units_month)} cobradas após a franquia de ${formatUnits(o.free_units_per_month)}`
      : `${formatUnits(o.billable_units_month)} cobradas`;
  return {
    key: "month",
    label: "Custo estimado do mês",
    value: formatUsd(o.estimated_cost_month_usd),
    subLabel: `${plural(o.units_month, "chamada", "chamadas")} desde ${formatUtcDate(o.month_start)} · ${franquia}`,
  };
}

function rangeCard(o: OcrUsageSummary, range: InsightsRange): OcrCostCard {
  const partes = [
    `${plural(o.pages_in_range, "página", "páginas")} em ${plural(o.analyses_in_range, "arquivo", "arquivos")}`,
    `valor de tabela ${formatUsd(o.estimated_cost_in_range_list_usd)}`,
  ];
  if (o.failed_units_in_range > 0) partes.push(`${formatUnits(o.failed_units_in_range)} com falha`);
  return {
    key: "range",
    label: `Chamadas ao Vision ${RANGE_PHRASE[range]}`,
    value: formatUnits(o.units_in_range),
    subLabel: partes.join(" · "),
  };
}

function previousMonthCard(o: OcrUsageSummary): OcrCostCard {
  return {
    key: "previous_month",
    label: "Mês anterior",
    value: formatUsd(o.estimated_cost_previous_month_usd),
    subLabel: `${plural(o.units_previous_month, "chamada", "chamadas")} · ${formatUsd(o.price_usd_per_1000)} por mil`,
  };
}

function autoCard(o: OcrUsageSummary): OcrCostCard {
  if (!o.vision_enabled) {
    return {
      key: "auto",
      label: "Identificação automática",
      value: "Sem Vision",
      subLabel: "O Google Vision está desligado: nenhum arquivo é lido, pedido ou não.",
    };
  }
  return o.auto_enabled
    ? {
        key: "auto",
        label: "Identificação automática",
        value: "Ligada",
        subLabel: "Todo arquivo que fica pronto é lido. Desligue em Configurações › Visualizador.",
      }
    : {
        key: "auto",
        label: "Identificação automática",
        value: "Desligada",
        subLabel: "Só lê quando alguém pede, pelo botão ou pela integração. Ligue em Configurações › Visualizador.",
      };
}

export function ocrCostCards(o: OcrUsageSummary, range: InsightsRange): OcrCostCard[] {
  return [monthCard(o), rangeCard(o, range), previousMonthCard(o), autoCard(o)];
}
